import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Calendar,
  Users,
  Mail,
  Phone,
  User,
  FileText,
  CheckCircle2,
  ArrowLeft,
  Castle,
  Home,
} from 'lucide-react';
import { useEstate } from '../hooks/useEstates';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../services/api';
import { Button, LoadingSpinner } from '../components';
import type { BookingFormData, BookingPayload } from '../types';

export function BookingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    user,
    webApp,
    showBackButton,
    hideBackButton,
    onBackButtonClick,
    sendData,
    hapticFeedback,
  } = useTelegram();

  const { data: estate, isLoading: isLoadingEstate } = useEstate(id || '');
  const [isFlexible, setIsFlexible] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BookingFormData>({
    defaultValues: {
      flexibilityDays: 0,
      numberOfGuests: 1,
    },
  });

  // Pre-fill from Telegram user data
  useEffect(() => {
    if (user) {
      setValue('firstName', user.first_name);
      if (user.last_name) {
        setValue('lastName', user.last_name);
      }
    }
  }, [user, setValue]);

  // Handle back button
  useEffect(() => {
    showBackButton();
    const cleanup = onBackButtonClick(() => {
      navigate(`/estate/${id}`);
    });

    return () => {
      hideBackButton();
      cleanup();
    };
  }, [showBackButton, hideBackButton, onBackButtonClick, navigate, id]);

  const bookingMutation = useMutation({
    mutationFn: async (data: BookingFormData) => {
      if (!estate) throw new Error('Estate not found');

      let currentInitData = webApp?.initData || '';

      if (!currentInitData) {
        const tg = window.Telegram?.WebApp;
        currentInitData = tg?.initData || '';
      }

      if (currentInitData && currentInitData.length > 0) {
        api.setInitData(currentInitData);
        console.log('[BookingPage] Refreshed initData before booking request, length:', currentInitData.length);
      } else {
        console.error('[BookingPage] CRITICAL: No initData available from Telegram WebApp!', {
          webAppExists: !!webApp,
          webAppInitData: webApp?.initData,
          windowTgExists: !!window.Telegram?.WebApp,
          windowTgInitData: window.Telegram?.WebApp?.initData,
        });
        throw new Error('Telegram authentication data is not available. Please refresh the app.');
      }

      const payload: BookingPayload = {
        ...data,
        propertyId: estate.id,
        propertyName: estate.name,
        telegramUserId: user?.id,
        telegramUsername: user?.username,
      };

      return api.submitBooking(payload);
    },
    onSuccess: (response) => {
      hapticFeedback?.notificationOccurred('success');
      setIsSuccess(true);

      sendData({
        type: 'booking_success',
        propertyId: estate?.id,
        propertyName: estate?.name,
        bookingId: response.booking.id,
      });
    },
    onError: (error: Error) => {
      hapticFeedback?.notificationOccurred('error');
      console.error('Booking submission error:', error);
    },
  });

  const onSubmit = (data: BookingFormData) => {
    bookingMutation.mutate(data);
  };

  if (isLoadingEstate) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!estate) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-serif text-2xl text-foreground mb-4">Estate not found</h1>
          <Button variant="glass" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  // Success state - mirrors web-2's success screen
  if (isSuccess) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 animate-fade-in">
        <div className="max-w-xl w-full text-center space-y-8 p-10 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl">
          <div className="flex justify-center">
            <div className="h-24 w-24 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-serif font-medium text-foreground">
              Request Received
            </h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              Thank you for choosing {estate.name}. Our concierge team has received your request and will contact you within 24 hours to finalize your stay.
            </p>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="primary"
              className="w-full sm:w-auto"
              onClick={() => navigate('/')}
            >
              <Castle className="mr-2 h-5 w-5" />
              Back to Castles
            </Button>
            <Button
              variant="glass"
              className="w-full sm:w-auto"
              onClick={() => navigate('/')}
            >
              <Home className="mr-2 h-5 w-5" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const inputClasses =
    'w-full px-3 py-2.5 border rounded-sm bg-white/5 text-foreground outline-none transition focus:border-white/30 focus:bg-white/10';
  const labelClasses =
    'block text-xs font-semibold uppercase tracking-widest text-slate-300 mb-1.5';
  const errorInputClasses = 'border-destructive/60';
  const normalInputClasses = 'border-white/15';

  const BackLink = () => (
    <button
      onClick={() => navigate(`/estate/${id}`)}
      className="inline-flex items-center gap-2 px-3 py-2 text-foreground/90 hover:text-foreground hover:bg-white/10 rounded-sm transition-all"
    >
      <ArrowLeft className="w-5 h-5" />
      <span className="text-sm font-medium tracking-wide">Back to Castles</span>
    </button>
  );

  return (
    <div className="pb-12 animate-fade-in text-foreground">
      <div className="mx-auto max-w-3xl px-6 pt-6">
        <BackLink />

        {/* Header */}
        <header className="mt-6 mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-300 mb-2">
            Book Your Stay
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-foreground">
            {estate.name}
          </h1>
          <p className="mt-3 text-sm text-slate-300">
            Tell us your preferences and our concierge team will contact you shortly.
          </p>
        </header>

        {/* Warning if not in Telegram context */}
        {!webApp && (
          <div className="mb-6 border border-yellow-400/30 bg-yellow-400/10 rounded-sm p-4 text-sm text-yellow-200">
            For the best experience, please open this app from Telegram. Some features may not work when accessed directly.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Name fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
            <div>
              <label className={labelClasses}>
                <User className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                First Name
              </label>
              <input
                {...register('firstName', { required: 'Required' })}
                className={`${inputClasses} ${errors.firstName ? errorInputClasses : normalInputClasses}`}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-destructive text-xs mt-1.5">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label className={labelClasses}>Last Name</label>
              <input
                {...register('lastName', { required: 'Required' })}
                className={`${inputClasses} ${errors.lastName ? errorInputClasses : normalInputClasses}`}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-destructive text-xs mt-1.5">{errors.lastName.message}</p>
              )}
            </div>

            <div>
              <label className={labelClasses}>
                <Mail className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Email
              </label>
              <input
                {...register('email', {
                  required: 'Required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email',
                  },
                })}
                type="email"
                className={`${inputClasses} ${errors.email ? errorInputClasses : normalInputClasses}`}
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="text-destructive text-xs mt-1.5">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className={labelClasses}>
                <Phone className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Phone
              </label>
              <input
                {...register('phone', { required: 'Required' })}
                type="tel"
                className={`${inputClasses} ${errors.phone ? errorInputClasses : normalInputClasses}`}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone && (
                <p className="text-destructive text-xs mt-1.5">{errors.phone.message}</p>
              )}
            </div>
          </div>

          {/* Date preference toggle */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setIsFlexible(false)}
              className={`py-3 px-4 rounded-sm border text-sm font-medium tracking-wide transition-all ${
                !isFlexible
                  ? 'bg-foreground text-primary border-foreground'
                  : 'bg-white/5 text-slate-300 border-white/15 hover:border-white/30 hover:bg-white/10'
              }`}
            >
              Exact Dates
            </button>
            <button
              type="button"
              onClick={() => setIsFlexible(true)}
              className={`py-3 px-4 rounded-sm border text-sm font-medium tracking-wide transition-all ${
                isFlexible
                  ? 'bg-foreground text-primary border-foreground'
                  : 'bg-white/5 text-slate-300 border-white/15 hover:border-white/30 hover:bg-white/10'
              }`}
            >
              Flexible Dates
            </button>
          </div>

          {/* Date fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
            <div>
              <label className={labelClasses}>
                <Calendar className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Check-in
              </label>
              <input
                {...register('startDate', { required: 'Required' })}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                className={`${inputClasses} ${errors.startDate ? errorInputClasses : normalInputClasses}`}
              />
            </div>

            <div>
              <label className={labelClasses}>
                <Calendar className="w-3 h-3 inline mr-1.5 -mt-0.5" />
                Check-out
              </label>
              <input
                {...register('endDate', { required: 'Required' })}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                className={`${inputClasses} ${errors.endDate ? errorInputClasses : normalInputClasses}`}
              />
            </div>
          </div>

          {/* Flexibility days */}
          {isFlexible && (
            <div>
              <label className={labelClasses}>Flexibility (+/- days)</label>
              <select
                {...register('flexibilityDays', { valueAsNumber: true })}
                className={`${inputClasses} ${normalInputClasses}`}
              >
                {[1, 2, 3, 4, 5, 7, 14].map((days) => (
                  <option key={days} value={days} className="bg-primary text-foreground">
                    {days} {days === 1 ? 'day' : 'days'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Number of guests */}
          <div>
            <label className={labelClasses}>
              <Users className="w-3 h-3 inline mr-1.5 -mt-0.5" />
              Number of Guests
            </label>
            <input
              {...register('numberOfGuests', {
                required: 'Required',
                valueAsNumber: true,
                min: { value: 1, message: 'Min 1 guest' },
                max: { value: 100, message: 'Max 100 guests' },
              })}
              type="number"
              min={1}
              max={100}
              className={`${inputClasses} ${errors.numberOfGuests ? errorInputClasses : normalInputClasses}`}
            />
            {errors.numberOfGuests && (
              <p className="text-destructive text-xs mt-1.5">{errors.numberOfGuests.message}</p>
            )}
          </div>

          {/* Special requests */}
          <div>
            <label className={labelClasses}>
              <FileText className="w-3 h-3 inline mr-1.5 -mt-0.5" />
              Special Requests (optional)
            </label>
            <textarea
              {...register('specialRequests', { maxLength: 1000 })}
              rows={4}
              className={`${inputClasses} ${normalInputClasses} resize-none`}
              placeholder="Share dietary needs, celebration plans, transfer requests, or anything else."
            />
            <p className="mt-1.5 text-xs text-slate-400">
              Optional: this helps us tailor your stay.
            </p>
          </div>

          {/* Error message */}
          {bookingMutation.isError && (
            <div className="border border-destructive/40 bg-destructive/10 rounded-sm p-4">
              <p className="text-destructive text-sm font-medium mb-1">
                Failed to submit enquiry
              </p>
              <p className="text-destructive/80 text-xs">
                {bookingMutation.error instanceof Error
                  ? bookingMutation.error.message
                  : 'Please try again or contact support if the issue persists.'}
              </p>
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full font-serif"
            isLoading={bookingMutation.isPending}
          >
            {bookingMutation.isPending ? 'Submitting...' : 'Submit Booking Request'}
          </Button>
        </form>

        {/* Bottom back */}
        <div className="pt-6">
          <BackLink />
        </div>
      </div>
    </div>
  );
}
